import { useId, useRef, useState } from "preact/hooks";
import {
  ArrowUpTrayIcon,
  ClipboardDocumentCheckIcon,
  PhotoIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useConfirm } from "../lib/use-confirm";
import { useImageGallery } from "../lib/use-image-gallery";
import { useModalDialog } from "../lib/use-modal-dialog";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  EmptyState,
  IconButton,
  Skeleton,
} from "./ui";
import type { ImageDto } from "@shared/types";

// Per-image thumbnail width. Doubled for retina via the dpr CDN param so the
// grid stays crisp without bloating the modal's network footprint. The 480px
// effective width lands exactly on the first step of the /img/ width ladder
// (see apps/api/src/lib/image-variant.ts), so thumbnails reuse the same
// Cloudflare transformation as any other small variant.
const THUMB_WIDTH = 240;

function displayImageUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
}

export function ImageModal({
  projectIdOrSlug,
  open,
  onClose,
}: {
  projectIdOrSlug: string;
  open: boolean;
  onClose: () => void;
}) {
  const ref = useModalDialog({ open, onClose });
  const titleId = useId();
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { confirm, dialog: confirmDialog } = useConfirm();
  const {
    items,
    loadError,
    uploadError,
    batchProgress,
    deleting,
    copied,
    uploading,
    uploadFiles,
    deleteImage,
    copyImage,
  } = useImageGallery({ projectIdOrSlug, open });

  function onBackdropClick(e: MouseEvent) {
    if (e.target === ref.current && !uploading && deleting === null) {
      ref.current?.close();
    }
  }

  function onCancel(e: Event) {
    if (uploading) e.preventDefault();
  }

  async function onPickFiles(files: FileList | File[]) {
    const accepted = await uploadFiles(files);
    if (accepted && fileInputRef.current) fileInputRef.current.value = "";
  }

  async function onDelete(item: ImageDto) {
    const ok = await confirm({
      title: "この画像を削除しますか？",
      description: (
        <>
          <span class="font-mono text-xs">{item.filename}</span>{" "}
          を R2 と DB の両方から削除します。記事から参照されている場合は表示が壊れます。
        </>
      ),
      confirmLabel: "削除する",
      tone: "danger",
    });
    if (!ok) return;
    await deleteImage(item);
  }

  return (
    <>
      {confirmDialog}
      <dialog
      ref={ref}
      onClick={onBackdropClick}
      onCancel={onCancel}
      aria-labelledby={titleId}
      class="m-auto w-[min(95vw,56rem)] max-h-[90vh] rounded-lg border-0 bg-transparent p-0 backdrop:bg-fg/40"
    >
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!dragOver) setDragOver(true);
        }}
        onDragLeave={(e) => {
          // Only clear when leaving the outermost element — otherwise hovering
          // a child fires leave→enter and flickers the highlight.
          if (e.target === e.currentTarget) setDragOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const files = e.dataTransfer?.files;
          if (files?.length) void onPickFiles(files);
        }}
        class={`rounded-lg transition ${
          dragOver ? "ring-2 ring-accent ring-offset-2 ring-offset-bg" : ""
        }`}
      >
        <Card elevated class="flex max-h-[90vh] flex-col">
        <CardHeader class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-2">
            <PhotoIcon class="h-5 w-5 text-fg-subtle" />
            <h2 id={titleId} class="text-base font-semibold text-fg">
              画像
            </h2>
          </div>
          <IconButton
            ariaLabel="閉じる"
            onClick={() => ref.current?.close()}
            disabled={uploading}
          >
            <XMarkIcon class="h-4 w-4" />
          </IconButton>
        </CardHeader>

        <CardBody class="min-h-0 flex-1 overflow-auto">
          {uploadError && (
            <Alert tone="danger" class="mb-3">
              {uploadError}
            </Alert>
          )}
          {loadError && <Alert tone="danger">{loadError}</Alert>}

          {items === null && !loadError && (
            <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} class="aspect-square w-full" />
              ))}
            </div>
          )}

          {items !== null && items.length === 0 && (
            <EmptyState
              icon={<PhotoIcon class="h-5 w-5" />}
              title="まだ画像がありません"
              description="下の「ファイルを追加」から画像をアップロードできます。"
            />
          )}

          {items !== null && items.length > 0 && (
            <ul class="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {items.map((item) => {
                const isCopied = copied === item.r2Key;
                const isDeleting = deleting === item.r2Key;
                // Build the thumbnail URL by re-using the canonical /img URL
                // and appending sizing query params. dpr=2 covers retina.
                const displayUrl = displayImageUrl(item.url);
                const sep = displayUrl.includes("?") ? "&" : "?";
                const thumbUrl = `${displayUrl}${sep}w=${THUMB_WIDTH}&dpr=2`;
                return (
                  <li
                    key={item.r2Key}
                    class="group relative overflow-hidden rounded-lg border border-border bg-bg-subtle/40"
                  >
                    <button
                      type="button"
                      onClick={() => void copyImage(item)}
                      disabled={isDeleting}
                      class="block aspect-square w-full bg-bg-inset disabled:opacity-50"
                      aria-label={`${item.filename} の URL をコピー`}
                    >
                      <img
                        src={thumbUrl}
                        alt=""
                        width={item.width ?? undefined}
                        height={item.height ?? undefined}
                        loading="lazy"
                        class="h-full w-full object-cover"
                      />
                    </button>
                    {isCopied && (
                      <div class="pointer-events-none absolute inset-0 flex items-center justify-center bg-fg/60 text-bg">
                        <ClipboardDocumentCheckIcon class="h-6 w-6" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => onDelete(item)}
                      disabled={isDeleting}
                      aria-label={`${item.filename} を削除`}
                      // ::after widens the hit area to 40px (28px box +
                      // -inset-1.5); it stops exactly at the tile corner.
                      class="pointer-coarse:opacity-100 absolute right-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-bg text-fg-subtle opacity-0 shadow-xs transition after:absolute after:-inset-1.5 hover:text-danger-fg focus-visible:opacity-100 group-hover:opacity-100 disabled:opacity-50"
                    >
                      <TrashIcon class="h-3.5 w-3.5" />
                    </button>
                    <div class="truncate border-t border-border-subtle bg-bg px-2 py-1.5 font-mono text-[11px] text-fg-subtle">
                      {item.filename}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>

        <CardFooter class="flex items-center justify-between gap-2">
          <p class="text-[11px] text-fg-faint">
            画像をクリックすると URL がコピーされます。ドラッグ&amp;ドロップでも追加できます。
          </p>
          <div class="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/png,image/jpeg,image/webp,image/gif"
              class="hidden"
              onChange={(e) => {
                const fs = (e.target as HTMLInputElement).files;
                if (fs && fs.length > 0) void onPickFiles(fs);
              }}
            />
            <Button
              variant="primary"
              size="sm"
              iconLeft={<ArrowUpTrayIcon class="h-3.5 w-3.5" />}
              loading={uploading}
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading
                ? batchProgress && batchProgress.total > 1
                  ? `アップロード中… ${batchProgress.done}/${batchProgress.total}`
                  : "アップロード中…"
                : "ファイルを追加"}
            </Button>
          </div>
        </CardFooter>
        </Card>
      </div>
    </dialog>
    </>
  );
}
